import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const baseUrl = (apiAddr) => (apiAddr || "").replace(/\/$/, "");

const JSON_ERROR_MSG = "서버가 데이터 대신 오류 페이지를 반환했습니다. 잠시 후 다시 시도해 주세요.";

/**
 * JSON API 호출: 응답이 HTML 등이면 파싱하지 않고 예외 또는 fallback 반환
 * @param {string} url
 * @param {{ fallback?: any }} opts - fallback 있으면 예외 대신 해당 값 반환
 */
async function fetchJsonSafe(url, opts = {}) {
  const { fallback } = opts;
  const r = await fetch(url);
  const ct = (r.headers.get("content-type") || "").toLowerCase();
  if (!r.ok || !ct.includes("application/json")) {
    if (fallback !== undefined) return fallback;
    throw new Error(JSON_ERROR_MSG);
  }
  try {
    return await r.json();
  } catch (e) {
    if (fallback !== undefined) return fallback;
    throw new Error(JSON_ERROR_MSG);
  }
}

/**
 * 사후 분석 리포트 엑셀 다운로드
 * @param {string} apiAddr - API 베이스 주소
 * @param {string} dsid - 재난 ID
 * @param {string} dsName - 재난명 (파일명 등에 사용)
 */
export async function exportReportExcel(apiAddr, dsid, dsName = "") {
  if (!baseUrl(apiAddr) || !dsid) {
    throw new Error("API 주소와 재난 선택이 필요합니다.");
  }
  const prefix = baseUrl(apiAddr);
  const excelReportUrl = `${prefix}/get/excelReport?dsid=${encodeURIComponent(dsid)}`;
  let summaryRes, rdvRes, brdRes, historyRes, activityRes, mfRes;
  try {
    const unified = await fetchJsonSafe(excelReportUrl);
    if (unified && unified.summary && Array.isArray(unified.rdvList) && Array.isArray(unified.brdList) && Array.isArray(unified.historyList) && Array.isArray(unified.mfList)) {
      summaryRes = unified.summary;
      rdvRes = unified.rdvList;
      brdRes = unified.brdList;
      historyRes = unified.historyList;
      mfRes = unified.mfList;
      activityRes = Array.isArray(unified.activitySummary) ? unified.activitySummary : null;
    } else {
      throw new Error("Invalid response");
    }
  } catch (_) {
    try {
      summaryRes = await fetchJsonSafe(`${prefix}/get/dashboardSummary?dsid=${encodeURIComponent(dsid)}`);
    } catch (e) {
      throw e.message === JSON_ERROR_MSG ? e : new Error(JSON_ERROR_MSG);
    }
    const rdvSummaryUrl = `${prefix}/get/rdvattendanceSummary?dsid=${encodeURIComponent(dsid)}&all=1`;
    const fetchRdvSummary = async () => {
      try {
        const body = await fetchJsonSafe(rdvSummaryUrl, { fallback: [] });
        if (Array.isArray(body)) return body;
        return body?.recordset ?? body?.data ?? body?.items ?? [];
      } catch (__) {
        return [];
      }
    };
    [rdvRes, brdRes, historyRes, activityRes, mfRes] = await Promise.all([
      fetchRdvSummary(),
      fetchJsonSafe(`${prefix}/get/getbrdmsg?dsid=${encodeURIComponent(dsid)}`, { fallback: [] }).catch(() => []),
      fetchJsonSafe(`${prefix}/get/mfhistory?dsid=${encodeURIComponent(dsid)}`, { fallback: [] }).catch(() => []),
      fetchJsonSafe(`${prefix}/get/activitySummary?dsid=${encodeURIComponent(dsid)}`, { fallback: null }).catch(() => null),
      fetchJsonSafe(`${prefix}/get/mflist?dsid=${encodeURIComponent(dsid)}`, { fallback: [] }).catch(() => []),
    ]);
  }

  const workbook = new ExcelJS.Workbook();

  const sb = summaryRes.signboard || {};
  const ws1 = workbook.addWorksheet("동원 요약");
  ws1.addRow(["구분", "인원"]);
  ws1.addRow(["전체", sb.totl ?? 0]);
  ws1.addRow(["집결중", sb.strt ?? 0]);
  ws1.addRow(["대기", sb.wait ?? 0]);
  ws1.addRow(["이동", sb.move ?? 0]);
  ws1.addRow(["활동", sb.actv ?? 0]);
  ws1.addRow(["복귀", sb.rtrn ?? 0]);
  ws1.getRow(1).font = { bold: true };

  const mfList = Array.isArray(mfRes) ? mfRes : [];
  const STATUS_LABEL = { 0: "집결중", 1: "대기", 2: "이동", 3: "활동", 4: "복귀", 9: "기타" };
  const wsMf = workbook.addWorksheet("동원자원 상세");
  wsMf.addRow(["순번", "소속", "장비", "장비명", "상태", "수량", "등록일시", "상태변경일시"]);
  wsMf.getRow(1).font = { bold: true };
  mfList.forEach((r) => {
    wsMf.addRow([
      r.seqnum ?? "",
      r.mfdorg ?? "",
      r.mfeqp ?? "",
      r.mfcsgn ?? "",
      STATUS_LABEL[r.mfstatus] ?? r.mfstatus ?? "",
      r.mfsumnum ?? "",
      r.mfipdt ? new Date(r.mfipdt).toLocaleString("ko-KR") : "",
      r.mfchdt ? new Date(r.mfchdt).toLocaleString("ko-KR") : "",
    ]);
  });

  const rdvList = Array.isArray(rdvRes) ? rdvRes : [];
  const ws2 = workbook.addWorksheet("지원인력 요약");
  ws2.addRow(["집결지", "소속", "소방/비소방", "이름", "출근시각", "퇴근시각", "임무"]);
  ws2.getRow(1).font = { bold: true };
  rdvList.forEach((r) => {
    ws2.addRow([
      r.rname ?? "",
      r.org ?? r.porg ?? "",
      r.person_type === "fire" ? "소방" : "비소방",
      r.name ?? r.pname ?? "",
      r.check_in_dt ?? "",
      r.check_out_dt ?? "",
      r.duty_desc ?? "",
    ]);
  });

  const ws2Detail = workbook.addWorksheet("지원인력 상세");
  ws2Detail.addRow([
    "순번",
    "집결지",
    "소속",
    "소방/비소방",
    "이름",
    "인사코드",
    "부서",
    "직급",
    "연락처",
    "임무",
    "출근시각",
    "퇴근시각",
    "등록일시",
  ]);
  ws2Detail.getRow(1).font = { bold: true };
  rdvList.forEach((r) => {
    ws2Detail.addRow([
      r.snum ?? "",
      r.rname ?? "",
      r.org ?? r.porg ?? "",
      r.person_type === "fire" ? "소방" : "비소방",
      r.name ?? r.pname ?? "",
      r.insa_code ?? "",
      r.pdept ?? "",
      r.prank ?? r.insa_rank ?? "",
      r.pphone ?? "",
      r.duty_desc ?? "",
      r.check_in_dt ?? "",
      r.check_out_dt ?? "",
      r.ipdt ? new Date(r.ipdt).toLocaleString("ko-KR") : "",
    ]);
  });

  const ws3 = workbook.addWorksheet("채팅·공지 요약");
  ws3.addRow(["항목", "건수"]);
  ws3.addRow(["공지", summaryRes.brdCount ?? 0]);
  ws3.addRow(["채팅", summaryRes.chatCount ?? 0]);
  ws3.getRow(1).font = { bold: true };
  const brdList = Array.isArray(brdRes) ? brdRes : [];
  if (brdList.length > 0) {
    ws3.addRow([]);
    ws3.addRow(["공지 목록 (최근)", ""]);
    ws3.addRow(["시간", "내용"]);
    brdList.slice(0, 50).forEach((m) => {
      ws3.addRow([m.ipdt ?? m.snum ?? "", (m.ctxt || m.content || "").toString().slice(0, 200)]);
    });
  }

  const kindLabel = (k) => (k === "order" ? "지령" : k === "status" ? "상태" : k === "memo" ? "메모" : k === "support" ? "지원" : k === "handover" ? "인수인계" : k || "기타");
  const histList = Array.isArray(historyRes) ? historyRes : [];
  const ws4 = workbook.addWorksheet("이력 요약");
  ws4.addRow(["시간", "구분", "장비/소속", "내용"]);
  ws4.getRow(1).font = { bold: true };
  histList.forEach((r) => {
    ws4.addRow([
      r.ipdt ?? "",
      kindLabel(r.kind),
      [r.mfeqp, r.mfdorg, r.mfcsgn].filter(Boolean).join(" / ") || "-",
      (r.content || "").toString().slice(0, 200),
    ]);
  });

  const activityRaw = activityRes?.items ?? activityRes?.summary ?? (Array.isArray(activityRes) ? activityRes : []);
  const activityList = Array.isArray(activityRaw) ? activityRaw : [];
  const ws5 = workbook.addWorksheet("소방실적 요약");
  const qKeys = activityList.reduce((acc, r) => {
    (r.quantities && Object.keys(r.quantities)) || [].forEach((k) => { if (!acc.includes(k)) acc.push(k); });
    return acc;
  }, []);
  ws5.addRow(["활동 유형", "건수", ...qKeys, "활동시간(분)"]);
  ws5.getRow(1).font = { bold: true };
  if (activityList.length > 0) {
    activityList.forEach((row) => {
      ws5.addRow([
        row.label_kr ?? row.label ?? row.activity_code ?? "-",
        row.total_count ?? row.count ?? 0,
        ...qKeys.map((k) => (row.quantities && row.quantities[k] != null ? row.quantities[k] : "")),
        row.total_duration_minutes ?? row.duration_minutes ?? "",
      ]);
    });
  } else {
    ws5.addRow(["실적 데이터 없음 (get/activitySummary 연동 후 표시)", "", ""]);
  }

  // 소방실적 상세: 유형별 시트 추가 (건수/계량이 있는 유형만)
  const activityTypesWithData = activityList.filter(
    (row) => (row.total_count ?? row.count ?? 0) > 0 || (row.quantities && Object.values(row.quantities).some((v) => v > 0))
  );
  for (const row of activityTypesWithData) {
    const code = row.activity_code ?? row.code ?? "";
    const labelKr = (row.label_kr ?? row.label ?? row.activity_code ?? "실적").toString();
    if (!code) continue;
    try {
      const detailRes = await fetchJsonSafe(
        `${prefix}/get/activityDetail?dsid=${encodeURIComponent(dsid)}&activity_code=${encodeURIComponent(code)}`,
        { fallback: null }
      ).catch(() => null);
      const detailItems = detailRes?.items ?? [];
      const quantityLabels = detailRes?.quantityLabels ?? [];
      const sheetName = ("실적_" + labelKr).replace(/[/\\?*:\[\]]/g, "_").slice(0, 31);
      const wsDetail = workbook.addWorksheet(sheetName);
      wsDetail.addRow(["작전지역", "소속", "동원자원명", "출처(채팅, 지령 등)", "해당 텍스트", ...quantityLabels, "입력시간"]);
      wsDetail.getRow(1).font = { bold: true };
      detailItems.forEach((item) => {
        const aff = (item.affiliation != null && String(item.affiliation).trim() !== "")
          ? item.affiliation
          : (() => {
              const rn = (item.resourceName || "").toString().trim();
              if (!rn) return "-";
              const first = rn.split(/\s+/)[0] || "";
              return (first && String(first).trim() !== "") ? first : "-";
            })();
        wsDetail.addRow([
          item.region ?? "-",
          aff,
          item.resourceName ?? "-",
          item.source ?? "-",
          (item.text ?? "-").toString().slice(0, 500),
          ...quantityLabels.map((l) => (item.quantities && item.quantities[l] != null ? item.quantities[l] : "")),
          item.inputTime ? new Date(item.inputTime).toLocaleString("ko-KR") : "-",
        ]);
      });
    } catch (_) {
      // 시트 추가 실패 시 해당 유형만 스킵
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `사후분석리포트_${(dsName || dsid).replace(/[/\\?*:]/g, "_")}_${Date.now()}.xlsx`;
  link.click();
  window.URL.revokeObjectURL(url);
}

/**
 * 사후 분석 리포트 PDF 다운로드 (요약 1~2페이지)
 * @param {string} apiAddr - API 베이스 주소
 * @param {string} dsid - 재난 ID
 * @param {string} dsName - 재난명
 * @param {object} summary - dashboardSummary 응답 (이미 있으면 재사용, 없으면 fetch)
 */
export async function exportReportPdf(apiAddr, dsid, dsName = "", summary = null) {
  if (!baseUrl(apiAddr) || !dsid) {
    throw new Error("API 주소와 재난 선택이 필요합니다.");
  }
  const prefix = baseUrl(apiAddr);
  const data = summary != null ? summary : await fetch(`${prefix}/get/dashboardSummary?dsid=${encodeURIComponent(dsid)}`).then((r) => r.json());
  const sb = data.signboard || {};
  const rc = data.rdvCount || {};
  const hist = data.historyCounts || {};
  const historyKindLabel = (k) => ({ memo: "메모", order: "지령", status: "상태", support: "지원", handover: "인수인계" }[k] ?? k);
  const histRows = Object.entries(hist).map(([k, v]) => `${historyKindLabel(k)}: ${v}`).join(", ") || "없음";

  let activityList = [];
  try {
    const activityRes = await fetch(`${prefix}/get/activitySummary?dsid=${encodeURIComponent(dsid)}`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    const activityRaw = activityRes?.items ?? activityRes?.summary ?? (Array.isArray(activityRes) ? activityRes : []);
    activityList = Array.isArray(activityRaw) ? activityRaw : [];
  } catch (_) {}
  const pdfQKeys = activityList.reduce((acc, r) => {
    (r.quantities && Object.keys(r.quantities)) || [].forEach((k) => { if (!acc.includes(k)) acc.push(k); });
    return acc;
  }, []);
  const activityHeader = `<tr style="background:#f5f5f5;"><th style="text-align:left;padding:8px;border:1px solid #ddd;">활동 유형</th><th style="text-align:right;padding:8px;border:1px solid #ddd;">건수</th>${pdfQKeys.map((k) => `<th style="text-align:right;padding:8px;border:1px solid #ddd;">${k.replace(/</g, "&lt;")}</th>`).join("")}<th style="text-align:right;padding:8px;border:1px solid #ddd;">활동시간(분)</th></tr>`;
  const activityRows =
    activityList.length > 0
      ? activityList
          .map(
            (row) =>
              `<tr><td style="padding:8px;border:1px solid #ddd;">${(row.label_kr ?? row.label ?? row.activity_code ?? "-").toString().replace(/</g, "&lt;")}</td><td style="text-align:right;padding:8px;border:1px solid #ddd;">${row.total_count ?? row.count ?? 0}</td>${pdfQKeys.map((k) => `<td style="text-align:right;padding:8px;border:1px solid #ddd;">${(row.quantities && row.quantities[k] != null) ? row.quantities[k] : "-"}</td>`).join("")}<td style="text-align:right;padding:8px;border:1px solid #ddd;">${row.total_duration_minutes ?? row.duration_minutes ?? "-"}</td></tr>`
          )
          .join("")
      : `<tr><td colspan="${3 + pdfQKeys.length}" style="padding:8px;border:1px solid #ddd;color:#666;">실적 데이터 없음 (get/activitySummary 연동 후 표시)</td></tr>`;

  const container = document.createElement("div");
  container.id = "report-pdf-content";
  container.style.cssText = "position:fixed;left:-9999px;top:0;width:800px;padding:24px;background:#fff;font-family:Noto Sans KR,sans-serif;font-size:14px;color:#333;";
  container.innerHTML = `
    <h2 style="margin:0 0 16px 0;font-size:18px;">사후 분석 리포트 요약</h2>
    <p style="margin:0 0 8px 0;">재난: ${(dsName || dsid).replace(/</g, "&lt;")}</p>
    <p style="margin:0 0 16px 0;color:#666;">생성일시: ${new Date().toLocaleString("ko-KR")}</p>
    <table style="width:100%;border-collapse:collapse;margin-bottom:20px;">
      <tr style="background:#f5f5f5;"><th style="text-align:left;padding:8px;border:1px solid #ddd;">동원</th><th style="text-align:right;padding:8px;border:1px solid #ddd;">인원</th></tr>
      <tr><td style="padding:8px;border:1px solid #ddd;">전체</td><td style="text-align:right;padding:8px;border:1px solid #ddd;">${sb.totl ?? 0}</td></tr>
      <tr><td style="padding:8px;border:1px solid #ddd;">집결중/대기/이동/활동/복귀</td><td style="text-align:right;padding:8px;border:1px solid #ddd;">${[sb.strt, sb.wait, sb.move, sb.actv, sb.rtrn].join(" / ")}</td></tr>
    </table>
    <table style="width:100%;border-collapse:collapse;margin-bottom:20px;">
      <tr style="background:#f5f5f5;"><th style="text-align:left;padding:8px;border:1px solid #ddd;">지원인력</th><th style="text-align:right;padding:8px;border:1px solid #ddd;">인원</th></tr>
      <tr><td style="padding:8px;border:1px solid #ddd;">소방</td><td style="text-align:right;padding:8px;border:1px solid #ddd;">${rc.fireCount ?? 0}</td></tr>
      <tr><td style="padding:8px;border:1px solid #ddd;">비소방</td><td style="text-align:right;padding:8px;border:1px solid #ddd;">${rc.otherCount ?? 0}</td></tr>
    </table>
    <table style="width:100%;border-collapse:collapse;margin-bottom:20px;">
      <tr style="background:#f5f5f5;"><th style="text-align:left;padding:8px;border:1px solid #ddd;">채팅·공지</th><th style="text-align:right;padding:8px;border:1px solid #ddd;">건수</th></tr>
      <tr><td style="padding:8px;border:1px solid #ddd;">공지</td><td style="text-align:right;padding:8px;border:1px solid #ddd;">${data.brdCount ?? 0}</td></tr>
      <tr><td style="padding:8px;border:1px solid #ddd;">채팅</td><td style="text-align:right;padding:8px;border:1px solid #ddd;">${data.chatCount ?? 0}</td></tr>
    </table>
    <p style="margin:0;"><strong>이력 요약:</strong> ${histRows}</p>
    <table style="width:100%;border-collapse:collapse;margin-top:20px;">
      ${activityHeader}
      ${activityRows}
    </table>
  `;
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: "#ffffff", width: 800 });
    document.body.removeChild(container);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const w = pageW - 2 * margin;
    const h = (canvas.height * w) / canvas.width;
    pdf.addImage(imgData, "PNG", margin, margin, w, Math.min(h, pageH - 2 * margin));
    if (h > pageH - 2 * margin) {
      pdf.addPage();
      pdf.addImage(imgData, "PNG", margin, margin - (pageH - 2 * margin), w, h);
    }
    pdf.save(`사후분석리포트_${(dsName || dsid).replace(/[/\\?*:]/g, "_")}_${Date.now()}.pdf`);
  } catch (e) {
    document.body.removeChild(container);
    throw e;
  }
}
